import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Badge, Ic, PhoneFrame, QrPattern, Screen } from '../mock/primitives';
import { useContent } from '../siteContent';

function JoinScreen() {
  return <Screen label="شاشة طلب الاشتراك: اختيار الباقة وعدد الوجبات والحجم" className="mk-phone-screen">
    <div className="mk-ph-head"><small>مطعمك</small><b>اختر باقتك</b></div>
    <div className="mk-options">
      <span>باقة الأسبوع<small>7 أيام</small></span>
      <span className="is-on">باقة الشهر<small>26 يومًا</small></span>
      <span>باقة الأسبوعين<small>14 يومًا</small></span>
    </div>
    <small className="mk-label">عدد الوجبات يوميًا</small>
    <div className="mk-seg"><span>وجبة</span><span className="is-on">وجبتان</span><span>3 وجبات</span></div>
    <small className="mk-label">الحجم</small>
    <div className="mk-seg"><span>150 جرام</span><span className="is-on">200 جرام</span><span>250 جرام</span></div>
    <div className="mk-toggle-row"><span>مشروب يومي</span><i className="mk-switch is-on" /></div>
    <div className="mk-ph-foot"><span><small>الإجمالي</small><b>— ر.س</b></span><span className="mk-btn">إرسال الطلب</span></div>
  </Screen>;
}

function ApproveScreen() {
  return <Screen label="شاشة مراجعة طلب الاشتراك واعتماده وتفعيله" className="mk-phone-screen">
    <div className="mk-ph-head"><small>لوحة المطعم</small><b>طلبات الاشتراك</b></div>
    <div className="mk-request">
      <div className="mk-row"><span><b>سارة</b><small>باقة الشهر · 200 جرام · وجبتان</small></span><span className="mk-swap"><Badge status="pending" /><Badge status="active" /></span></div>
      <div className="mk-kv"><span>طريقة الاستلام</span><b>توصيل</b></div>
      <div className="mk-kv"><span>الدفع</span><b>إيصال التحويل مرفق</b></div>
      <div className="mk-kv"><span>البداية</span><b>الأحد</b></div>
      <span className="mk-btn mk-btn-block mk-approve">اعتماد وتفعيل</span>
    </div>
    <div className="mk-row mk-dim"><span><b>فهد</b><small>باقة الأسبوع · 150 جرام</small></span><Badge status="pending" /></div>
  </Screen>;
}

function CardScreen() {
  return <Screen label="بطاقة الاشتراك الرقمية للمشترك مع رمز QR" className="mk-phone-screen mk-card-screen">
    <div className="mk-subcard">
      <div className="mk-subcard-top"><span><small>مطعمك</small><b>سارة</b></span><Badge status="active" /></div>
      <QrPattern />
      <div className="mk-subcard-meta"><span><small>الباقة</small><b>باقة الشهر</b></span><span><small>الرصيد</small><b>52 وجبة</b></span></div>
      <small className="mk-code">MX-24817</small>
    </div>
    <div className="mk-card-actions"><span className="mk-chip">حفظ البطاقة</span><span className="mk-chip">مشاركة</span></div>
  </Screen>;
}

function RedeemScreen() {
  return <Screen label="صرف وجبة: مسح رمز QR والتحقق من الاشتراك وتسجيل الصرف" className="mk-phone-screen">
    <div className="mk-ph-head"><small>الفرع الرئيسي</small><b>صرف وجبة</b></div>
    <div className="mk-scanner"><QrPattern /><i className="mk-scanline" /></div>
    <div className="mk-redeem-result">
      <span className="mk-ok"><Ic name="scan" /></span>
      <span><b>تم تسجيل الصرف</b><small>سارة · باقة الشهر</small></span>
    </div>
    <div className="mk-kv"><span>الرصيد</span><b>52 ← 51 وجبة</b></div>
  </Screen>;
}

function TrackScreen() {
  return <Screen label="متابعة الاشتراك: الرصيد والمدة المتبقية والتجميد والتجديد" className="mk-phone-screen">
    <div className="mk-ph-head"><small>تفاصيل الاشتراك</small><b>سارة</b></div>
    <div className="mk-ring-wrap"><svg className="mk-ring" viewBox="0 0 120 120" aria-hidden="true"><circle cx="60" cy="60" r="50" /><circle className="mk-ring-value" cx="60" cy="60" r="50" pathLength="100" /></svg><span><b>51</b><small>وجبة متبقية</small></span></div>
    <div className="mk-kv"><span>المدة المتبقية</span><b>18 يومًا</b></div>
    <div className="mk-kv"><span>أيام التجميد المتاحة</span><b>5 أيام</b></div>
    <div className="mk-seg mk-seg-actions"><span>تجميد</span><span>تجديد</span></div>
    <div className="mk-history"><small>آخر العمليات</small><span>صرف وجبة · الفرع الرئيسي</span><span>صرف وجبة · توصيل</span></div>
  </Screen>;
}

const screens: ReactNode[] = [<JoinScreen key="join" />, <ApproveScreen key="approve" />, <CardScreen key="card" />, <RedeemScreen key="redeem" />, <TrackScreen key="track" />];

export function Journey() {
  const { journey } = useContent();
  const steps = journey.steps.slice(0, screens.length).map((step, index) => ({ ...step, screen: screens[index] }));
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLOListElement>(null);
  useEffect(() => {
    const items = listRef.current?.querySelectorAll<HTMLElement>('[data-step]');
    if (!items?.length) return;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => { if (entry.isIntersecting) setActive(Number((entry.target as HTMLElement).dataset.step)); });
    }, { rootMargin: '-45% 0px -45% 0px' });
    items.forEach(item => observer.observe(item));
    return () => observer.disconnect();
  }, [steps.length]);

  if (!journey.visible) return null;
  return <section id="journey" className="mx-journey" aria-labelledby="journey-title">
    <div className="mx-container">
      <header className="mx-section-head">
        <h2 id="journey-title" className="mx-h2"><span>{journey.line1}</span><span>{journey.line2}</span></h2>
        <p className="mx-body">{journey.body}</p>
      </header>
      <div className="mx-journey-layout">
        <ol ref={listRef} className="mx-steps">
          {steps.map((step, index) => <li key={index} data-step={index} className={`mx-step${active === index ? ' is-active' : ''}`}>
            <span className="mx-step-num" aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
            <div><h3>{step.title}</h3><p>{step.text}</p></div>
            <div className="mx-step-inline is-active"><PhoneFrame>{step.screen}</PhoneFrame></div>
          </li>)}
        </ol>
        <div className="mx-journey-stage" aria-live="off">
          <div className="mx-journey-sticky">
            <PhoneFrame className="mx-stage-phone">
              {steps.map((step, index) => <div key={index} className={`mx-stage-screen${active === index ? ' is-active' : ''}`} aria-hidden={active !== index}>{step.screen}</div>)}
            </PhoneFrame>
            <div className="mx-stage-dots" aria-hidden="true">{steps.map((_, index) => <i key={index} className={active === index ? 'is-on' : ''} />)}</div>
          </div>
        </div>
      </div>
      {journey.statement && <p className="mx-statement mx-statement-sm">{journey.statement}</p>}
    </div>
  </section>;
}
